import { useGetAccommodationTypes } from "@/actions/hooks/useGetAccommodationTypes.ts";
import Container from "@/components/Container/Container.tsx";
import { RangePicker } from "@/components/Inputs/RangePicker/RangePicker.tsx";
import { SelectOption } from "@/components/Inputs/Select/SelectProps.ts";
import { filterDates } from "@/helpers/filterDates.ts";
import { URLParam } from "@/helpers/interfaces/URLParam.ts";
import { useGetAccommodationTypeTranslated } from "@/helpers/translation/useGetAccommodationTypesTranslated.ts";
import { PickingHeaderProps } from "@/pages/AccommodationPicking/components/PickingHeader/PickingHeaderProps.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { RootAttribute } from "@/stores/EngineStore/enums/RootAttribute.ts";
import { useEffect, useMemo } from "react";

import { Select } from "@/components/Inputs/Select/Select.tsx";
import { getAPIUrl } from "@/stores/ApolloClient/helpers/getAPIUrl.ts";
import { IoIosSearch } from "react-icons/io";
import { useShallow } from "zustand/react/shallow";

export const PickingHeader = (props: PickingHeaderProps) => {
  const { accommodationTypes, getAccommodationTypesLoading } = useGetAccommodationTypes();
  const { getTranslatedAccommodationTypesData } = useGetAccommodationTypeTranslated();
  const {
    defaultDateRange,
    setDefaultDateRange,
    availableRateGroups,
    excludeDates,
    setAccommodationTypeId,
    accommodationTypeId,
    rootAttributes,
    resetCartItems,
  } = useEngineStore(
    useShallow((state) => ({
      defaultDateRange: state.defaultDateRange,
      setDefaultDateRange: state.setDefaultDateRange,
      availableRateGroups: state.availableRateGroups,
      excludeDates: state.rootAttributes[RootAttribute.ExcludeDates],
      setAccommodationTypeId: state.setAccommodationTypeId,
      accommodationTypeId: state.accommodationTypeId,
      rootAttributes: state.rootAttributes,
      resetCartItems: state.resetCartItems,
    })),
  );

  const hasExcludedType = useMemo(
    () => availableRateGroups.some((ARG) => !ARG.isParcel),
    [availableRateGroups],
  );

  const accommodationTypeOptions: SelectOption[] = useMemo(() => {
    return accommodationTypes?.map((accType) => {
      const translatedData = getTranslatedAccommodationTypesData(accType);
      return {
        id: accType.id,
        name: translatedData?.name || accType.name,
        code: accType.code,
      };
    });
  }, [accommodationTypes, getTranslatedAccommodationTypesData]);

  const handleSelectAccommodationType = (code: string) => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set(URLParam.AccommodationType, code);
    window.history.replaceState({}, "", `?${urlParams.toString()}`);

    setAccommodationTypeId(code);
  };

  useEffect(() => {
    if (defaultDateRange[0] && defaultDateRange[1]) {
      props.handleFetchAvailableRGs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDateRange, accommodationTypeId]);

  return (
    <section>
      <Container className={"headerContainer"}>
        <div
          className={`${
            rootAttributes[RootAttribute.HideCategoryPicker]
              ? "c-be-accommodation-picker-wrapper__no-category"
              : "c-be-accommodation-picker-wrapper"
          }`}
        >
          <div className="c-be-accommodation-picker-wrapper__left">
            <RangePicker
              onChange={(dates) => {
                setDefaultDateRange(dates);
                resetCartItems();
              }}
              startDate={defaultDateRange[0]}
              endDate={defaultDateRange[1]}
              filterDate={
                (getAPIUrl().includes("simuni") && hasExcludedType && excludeDates) ||
                getAPIUrl().includes("olea")
                  ? filterDates
                  : undefined
              }
            />
            {/*<div className="c-guest-count-input-wrap">*/}
            {/*  <GuestCountInput*/}
            {/*    // Note: Adults only are used as guests in the initial screen for searching*/}
            {/*    value={defaultAdults}*/}
            {/*    onChange={setDefaultAdults}*/}
            {/*  />*/}
            {/*</div>*/}
          </div>
          {rootAttributes?.[RootAttribute.HideCategoryPicker] ? null : (
            <div className="c-be-accommodation-picker-wrapper__center">
              <Select
                options={accommodationTypeOptions}
                onSelect={handleSelectAccommodationType}
                loading={getAccommodationTypesLoading}
                value={accommodationTypeId}
                optionValueKey="code"
              />
            </div>
          )}
          <div className="c-be-accommodation-picker-wrapper__right">
            <button
              disabled={!defaultDateRange[1]}
              className="c-be-button"
              onClick={props.handleFetchAvailableRGs}
            >
              <span className="c-be-button__label">
                <IoIosSearch fill={"white"} />
              </span>
            </button>
          </div>
        </div>
      </Container>
    </section>
  );
};
